<template>
  <div class="my-1 chart-wrapper">
    <b-button class="mt-1 mb-1" variant="outline-dark" @click="showListedTrendsTable = !showListedTrendsTable" :disabled="presetLoading"><i class="icon-list" /> - {{ listedTrendsCount }}</b-button>
    <b-button v-if="listedTrendsCount > 0" class="mt-1 mb-1 ml-1" variant="outline-danger" @click="clearTrendSelection" :disabled="presetLoading">{{ $t("other.clear") }}</b-button>
    <div v-show="showListedTrendsTable">
      <b-row v-if="listedTrendsCount > 0" class="mb-2">
        <b-col md="6" class="my-1">
          <b-form-group label-cols-sm="3" :label="$t('other.filter')" class="mb-0">
            <b-input-group>
              <b-form-input v-model="listedTrendsFilter" :placeholder="$t('other.searchHolder')"></b-form-input>
              <b-input-group-append>
                <b-button :disabled="!listedTrendsFilter" @click="listedTrendsFilter = ''">{{ $t('other.clear') }}</b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>
      <b-table
        show-empty
        responsive
        small
        striped
        hover
        :items="listedTrendsTableItems"
        :fields="selectedTrendsTableFields"
        :current-page="listedTrendsCurrentPage"
        :per-page="20"
        :filter="listedTrendsFilter"
        @filtered="onListedTrendsFiltered"
      >
        <template #cell(variable)="row">{{ row.item.variable }}</template>
        <template #cell(module)="row">{{ row.item.module }}</template>
        <template #cell(object)="row">{{ row.item.object }}</template>
        <template #cell(description)="row">{{ row.item.description }}</template>
        <template #cell(actions)="row">
          <b-button
            size="sm"
            variant="danger"
            @click="removeFromExtraTrends(row.item)"
          >
            <i class="icon-minus" />
          </b-button>
        </template>
      </b-table>
      <b-row v-if="listedTrendsCount > 20">
        <b-col md="6" class="my-1">
          <b-pagination
            v-model="listedTrendsCurrentPage"
            :total-rows="listedTrendsTotalRows"
            :per-page="20"
            class="my-0"
          ></b-pagination>
        </b-col>
      </b-row>
    </div>
    <b-button
      class="mt-3 mb-1"
      variant="success"
      block
      :disabled="presetGraphLoading || presetLoading"
      @click="onDrawGraphClick()"
    >{{ $t("project.graphViewer.drawGraph") }}</b-button>
    <b-modal
      v-model="showPresetGraphTooManyModal"
      :title="$t('project.graphViewer.presetGraphTooManyTitle')"
      ok-only
      ok-variant="danger"
      @ok="showPresetGraphTooManyModal = false"
      @hidden="showPresetGraphTooManyModal = false"
    >
      <p class="mb-0">{{ presetGraphError }}</p>
    </b-modal>
    <div v-if="errored" class="alert alert-danger">{{ errored }}</div>
    <div v-if="presetLoading" class="alert alert-info">{{ $t("project.graphViewer.presetLoading") }}</div>
    <div v-if="presetGraphLoading" class="alert alert-info">{{ $t("project.graphViewer.presetGraphLoading") }}</div>
    <div v-if="exportInProgress" class="alert alert-info">{{ $t("project.graphViewer.exportInProgress") }}</div>
    <div v-else-if="graphMode === 'custom'">
      <b-button
        size="sm"
        variant="outline-secondary"
        class="chart-panel-toggle text-left py-1 px-2 mb-1"
        block
        @click="showCustomTools = !showCustomTools"
      >
        <i :class="showCustomTools ? 'icon-arrow-up' : 'icon-arrow-down'" class="mr-1" />
        {{ $t("project.graphViewer.customToolsPanel") }}
      </b-button>
      <b-collapse v-model="showCustomTools" @shown="updateFitSize" @hidden="updateFitSize">
        <b-row class="mb-2">
          <b-col cols="12" sm="6" class="mb-1">
            <b-input-group
              size="sm"
              :prepend="$t('project.graphViewer.customFrom')"
              class="mt-0"
            >
              <b-form-input type="date" v-model="dateFrom"></b-form-input>
              <b-form-input type="time" v-model="timeFrom"></b-form-input>
            </b-input-group>
          </b-col>
          <b-col cols="12" sm="6" class="mb-1">
            <b-input-group
              size="sm"
              :prepend="$t('project.graphViewer.customTo')"
              class="mt-0"
            >
              <b-form-input type="date" v-model="dateTo"></b-form-input>
              <b-form-input type="time" v-model="timeTo"></b-form-input>
            </b-input-group>
          </b-col>
        </b-row>
        <b-row class="mb-1 align-items-end">
          <b-col cols="6" md="3" class="mb-1">
            <div class="small text-muted mb-1">{{ $t("project.graphViewer.customToolsExport") }}</div>
            <b-form-select size="sm" v-model="exportDelimeter">
              <option value=";">
                {{ $t("project.graphViewer.exportSemicolon") }}
              </option>
              <option value=",">
                {{ $t("project.graphViewer.exportComma") }}
              </option>
            </b-form-select>
          </b-col>
          <b-col cols="6" md="3" class="mb-1">
            <b-button size="sm" variant="outline-primary" @click="exportCustomData()" block :disabled="exportInProgress || presetGraphLoading || presetLoading">{{
              $t("project.graphViewer.customExport")
            }}</b-button>
          </b-col>
          <b-col cols="6" md="3" class="mb-1">
            <div class="small text-muted mb-1">{{ $t("project.graphViewer.customToolsConsumption") }}</div>
            <b-button-group size="sm" class="d-flex">
              <b-button variant="outline-dark" @click="exportPdfData()" :disabled="exportInProgress || presetLoading">PDF</b-button>
              <b-button variant="outline-primary" @click="exportPdfData(true)" :disabled="exportInProgress || presetLoading">CSV</b-button>
            </b-button-group>
          </b-col>
          <b-col cols="6" md="3" class="mb-1">
            <div class="small text-muted mb-1">{{ $t("project.graphViewer.customToolsReading") }}</div>
            <div class="d-flex">
              <b-button-group size="sm" class="flex-grow-1">
                <b-button variant="outline-dark" @click="exportPdfDataReading()" :disabled="exportInProgress || presetLoading">PDF</b-button>
                <b-button variant="outline-primary" @click="exportPdfDataReading(true)" :disabled="exportInProgress || presetLoading">CSV</b-button>
              </b-button-group>
              <b-form-input
                class="ml-1"
                size="sm"
                type="number"
                style="max-width: 4.5rem;"
                v-model="readingThreshold"
                :disabled="exportInProgress || presetLoading"
                :title="$t('project.graphViewer.pdfExportThreshold')"
              />
              <span class="small text-muted ml-1 align-self-center">min</span>
            </div>
          </b-col>
        </b-row>
      </b-collapse>
    </div>
    <div ref="graphDiv" style="background:#fff;width:100%;box-sizing:border-box;overflow:auto;" @mouseup="resizeChartBox">
      <b-button
        size="sm"
        variant="outline-secondary"
        class="chart-panel-toggle text-left py-1 px-2 mb-1"
        block
        @click="showChartSettings = !showChartSettings"
      >
        <i :class="showChartSettings ? 'icon-arrow-up' : 'icon-arrow-down'" class="mr-1" />
        {{ $t("project.graphViewer.chartSettingsPanel") }}
      </b-button>
      <b-collapse v-model="showChartSettings" @shown="updateFitSize" @hidden="updateFitSize">
        <div class="border-top pt-3">
          <b-row class="mb-2" style="margin-left:0; margin-right:0;">
            <b-col sm="12">
              <b-input-group>
                <b-form-input size="sm" type="number" v-model.number="yThresholdMax1" :placeholder="$t('project.graphViewer.yThresholdLine1')" />
                <b-form-input size="sm" type="number" v-model.number="yThresholdMax2" :placeholder="$t('project.graphViewer.yThresholdLine2')" />
                <b-form-input size="sm" type="number" v-model.number="yThresholdMin1" :placeholder="$t('project.graphViewer.yThresholdLine3')" />
                <b-form-input size="sm" type="number" v-model.number="yThresholdMin2" :placeholder="$t('project.graphViewer.yThresholdLine4')" />
                <b-input-group-append>
                  <b-button size="sm" variant="outline-primary" @click="setThresholds" v-b-tooltip.hover :title="$t('project.graphViewer.setThresholds')"><i class="icon-plus" /></b-button>
                  <b-button size="sm" variant="outline-secondary" v-b-popover.click.blur.top="{ content: $t('project.graphViewer.thresholdsHelp'), container: 'body' }"><i class="icon-question" /></b-button>
                </b-input-group-append>
              </b-input-group>
            </b-col>
          </b-row>
          <b-row class="mb-2" style="margin-left:0; margin-right:0;">
            <b-col sm="4" class="mb-1">
              <b-input-group>
                <b-form-input size="sm" type="number" v-model.number="yGraphMinimum" :placeholder="$t('project.graphViewer.graphYFrom')" />
                <b-form-input size="sm" type="number" v-model.number="yGraphMaximum" :placeholder="$t('project.graphViewer.graphYTo')" />
                <b-input-group-append>
                  <b-button size="sm" variant="outline-primary" @click="setRange(yGraphMinimum, yGraphMaximum)" v-b-tooltip.hover :title="$t('project.graphViewer.setRange')"><i class="icon-frame" /></b-button>
                  <b-button size="sm" variant="outline-secondary" v-b-popover.click.blur.top="{ content: $t('project.graphViewer.yRangeHelp'), container: 'body' }"><i class="icon-question" /></b-button>
                </b-input-group-append>
              </b-input-group>
            </b-col>
            <b-col sm="4" class="mb-1">
              <b-input-group>
                <b-form-input size="sm" type="number" v-model.number="graphCustomWidth" :placeholder="$t('project.graphViewer.graphWidth')" />
                <b-form-input size="sm" type="number" v-model.number="graphCustomHeight" :placeholder="$t('project.graphViewer.graphHeight')" />
                <b-input-group-append>
                  <b-button size="sm" variant="outline-primary" @click="setSize(false)" v-b-tooltip.hover :title="$t('project.graphViewer.setCustomSize')"><i class="icon-frame" /></b-button>
                  <b-button size="sm" variant="outline-danger" @click="setSize(true)" v-b-tooltip.hover :title="$t('project.graphViewer.resetSize')"><i class="icon-frame" /></b-button>
                  <b-button size="sm" variant="outline-secondary" v-b-popover.click.blur.top="{ content: $t('project.graphViewer.sizeHelp'), container: 'body' }"><i class="icon-question" /></b-button>
                </b-input-group-append>
              </b-input-group>
            </b-col>
            <b-col class="text-right" sm="4">
              <b-button size="sm" class="ml-1" variant="outline-dark" @click="downloadGraph" v-b-tooltip.hover :title="$t('project.graphViewer.downloadGraph')"><i class="icon-picture" /></b-button>
              <b-button size="sm" class="ml-1" variant="outline-dark" @click="printGraph" v-b-tooltip.hover :title="$t('project.graphViewer.printGraph')"><i class="icon-printer" /></b-button>
              <b-button size="sm" class="ml-1" variant="outline-dark" @click="toggleFullScreen" v-b-tooltip.hover :title="$t('project.graphViewer.toggleFullScreen')"><i class="icon-size-fullscreen" /></b-button>
              <b-button size="sm" class="ml-1" :variant="toggleZoomFlag ? 'outline-success' : 'outline-danger'" @click="toggleZoom" v-b-tooltip.hover :title="$t('project.graphViewer.toggleZoom')"><i class="icon-cursor-move" /></b-button>
              <b-button size="sm" class="ml-1" variant="outline-dark" @click="clearRange" v-b-tooltip.hover :title="$t('project.graphViewer.clearRange')"><i class="icon-refresh" /></b-button>
              <b-button size="sm" class="ml-1" variant="outline-warning" @click="resetZoom" v-b-tooltip.hover :title="$t('project.graphViewer.resetZoom')"><i class="icon-reload" /></b-button>
              <b-button size="sm" class="ml-1" variant="outline-secondary" v-b-popover.click.blur.left="{ html: true, content: iconActionsHelpHtml, container: 'body' }"><i class="icon-question" /></b-button>
            </b-col>
          </b-row>
        </div>
      </b-collapse>
      <lineChart
        ref="lineChartRef"
        class="chart-fit"
        chartId="scatter_chart_01"
        :chartData="dataCollection"
        :phUnit="selectedTrends.length > 0 ? selectedTrends[0].phUnit : null"
        :style="chartBoxStyle"
      />
    </div>
    <div v-if="informed" class="alert alert-success">{{informed}}</div>
  </div>
</template>

<script>
import LineChart from "./LineChart";
import { exportService, projectService } from "@/_services";
import {
  errorHandler,
  getRandomInt,
  csvExport,
  formatNumber,
  createInvoice,
  createCsvInvoice
} from "@/_helpers";

const colors = [
  "LIGHTCORAL",
  "ORANGERED",
  "DARKKHAKI",
  "DODGERBLUE",
  "SEAGREEN",
  "DARKRED",
  "OLIVE",
  "DARKCYAN",
  "STEELBLUE",
  "GOLDENROD",
  "INDIGO",
];

export default {
  name: "ChartManager",
  components: {
    lineChart: LineChart,
  },
  props: {
    variable: {
      type: Object,
      default: null,
    },
    graphMode: {
      type: String,
      default: "day",
    },
    projectId: {
      type: Number,
      default: null,
    },
    presetName: {
      type: String,
      default: "",
    },
    selectedTrendItems: {
      type: Array,
      default: null
    },
    presetLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dataCollection: { datasets: [] },
      selectedTrends: [],
      usedColors: [],
      trendColors: new Map(),
      dateFrom: null,
      timeFrom: null,
      dateTo: null,
      timeTo: null,
      errored: null,
      exportInProgress: false,
      exportDelimeter: ";",
      informed: null,
      projectName: null,
      readingThreshold: 900,
      yGraphMinimum: null,
      yGraphMaximum: null,
      yThresholdMax1: null,
      yThresholdMax2: null,
      yThresholdMin1: null,
      yThresholdMin2: null,
      graphCustomWidth: null,
      graphCustomHeight: null,
      graphCustomWidthConst: null,
      graphCustomHeightConst: null,
      lockCustomWidth: false,
      lockCustomHeight: false,
      fitChartHeight: 320,
      toggleZoomFlag: false,
      selectedTrendsTableFields: [
        { key: "module", label: "" },
        { key: "object", label: "" },
        { key: "description", label: "" },
        { key: "code", label: "ID" },
        { key: "actions", label: "" }
      ],
      presetGraphLoading: false,
      presetGraphError: null,
      showPresetGraphTooManyModal: false,
      maxPresetGraphTrends: 15,
      customSeriesCache: [],
      consumptionTrendsCache: [],
      listedTrendsFilter: null,
      listedTrendsCurrentPage: 1,
      listedTrendsTotalRows: 0,
      showListedTrendsTable: false,
      showCustomTools: false,
      showChartSettings: false
    };
  },
  watch: {
    variable: {
      immediate: true,
      handler(val) {
        this.errored = null;
        this.informed = null;
        this.usedColors = [];
        this.trendColors.clear();
        this.clearLoadedTrendData();
        if (val === null) {
          if (!this.selectedTrendItems || this.selectedTrendItems.length === 0) this.selectedTrends = [];
          return;
        }
        if (!this.selectedTrendItems || this.selectedTrendItems.length === 0) {
          this.selectedTrends = [{
            code: val.item1,
            variable: val.item2,
            shortUserDesc: val.item3,
            longUserDesc: val.item4,
            phUnit: val.item6 != null ? val.item6 : "",
            sortOrder: isNaN(val.item5) ? 0 : Number(val.item5),
            data: null,
            custom: null
          }];
        }
      },
    },
    selectedTrendItems: {
      handler: function(val) {
        this.listedTrendsCurrentPage = 1;
        this.clearLoadedTrendData();
        if (val != null && val.length > 0) {
          this.selectedTrends = val.map(function(item) {
            return {
              code: item.item1,
              variable: item.item2,
              shortUserDesc: item.item3,
              longUserDesc: item.item4,
              phUnit: item.item6 != null ? item.item6 : "",
              sortOrder: isNaN(item.item5) ? 0 : Number(item.item5),
              data: null,
              custom: null
            };
          });
        } else if (val != null && val.length === 0) {
          this.selectedTrends = [];
        } else if (this.variable && this.variable.item2) {
          this.selectedTrends = [{
            code: this.variable.item1,
            variable: this.variable.item2,
            shortUserDesc: this.variable.item3,
            longUserDesc: this.variable.item4,
            phUnit: this.variable.item6 != null ? this.variable.item6 : "",
            sortOrder: isNaN(this.variable.item5) ? 0 : Number(this.variable.item5),
            data: null,
            custom: null
          }];
        } else {
          this.selectedTrends = [];
        }
      },
      immediate: true
    },
    graphMode: {
      immediate: true,
      handler: function() {
        this.errored = null;
        this.informed = null;
        this.presetGraphError = null;
        this.clearLoadedTrendData();
        this.updateFitSize();
      }
    },
    showListedTrendsTable: function() {
      this.updateFitSize();
    },
    presetGraphLoading: function() {
      this.updateFitSize();
    },
    listedTrendsTableItems: {
      handler: function(items) {
        this.listedTrendsTotalRows = items.length;
      },
      immediate: true
    },
    dateFrom: function() { this.consumptionTrendsCache = []; },
    dateTo: function() { this.consumptionTrendsCache = []; },
    timeFrom: function() { this.consumptionTrendsCache = []; },
    timeTo: function() { this.consumptionTrendsCache = []; }
  },
  computed: {
    chartBoxStyle() {
      return {
        width: this.lockCustomWidth ? this.graphCustomWidthConst + "px" : "100%",
        height: (this.lockCustomHeight ? this.graphCustomHeightConst : this.fitChartHeight) + "px"
      };
    },
    iconActionsHelpHtml() {
      return [
        "<i class=\"icon-picture\"></i> " + this.$t("project.graphViewer.downloadGraph"),
        "<i class=\"icon-printer\"></i> " + this.$t("project.graphViewer.printGraph"),
        "<i class=\"icon-size-fullscreen\"></i> " + this.$t("project.graphViewer.toggleFullScreen"),
        "<i class=\"icon-cursor-move\"></i> " + this.$t("project.graphViewer.toggleZoom"),
        "<i class=\"icon-refresh\"></i> " + this.$t("project.graphViewer.clearRange"),
        "<i class=\"icon-reload\"></i> " + this.$t("project.graphViewer.resetZoom")
      ].join("<br>");
    },
    listedTrendsCount() {
      return this.listedTrendsTableItems.length;
    },
    listedTrendsTableItems() {
      return this.selectedTrends.map(function(item) {
        var variable = item.variable;
        var parts = variable ? variable.split(".") : ["", ""];
        var desc = item.shortUserDesc ? item.shortUserDesc : item.longUserDesc;
        return {
          code: item.code,
          variable: variable,
          module: parts[0] || "",
          object: parts[1] || "",
          description: desc || "",
          sortOrder: item.sortOrder
        };
      });
    }
  },
  created() {
    var now = new Date();
    now.setHours(now.getHours() + 1);
    now.setMinutes(0);
    let datetime = this.formatDateTime(now);
    this.dateTo = datetime[0];
    this.timeTo = datetime[1];
    now.setHours(now.getHours() - 8);
    datetime = this.formatDateTime(now);
    this.dateFrom = datetime[0];
    this.timeFrom = datetime[1];

    projectService
      .getProjectInfo(this.projectId)
      .then((response) => {
        this.projectName = response.data.projectname;
      })
      .catch((error) => {
        this.errored = errorHandler(error);
      });
  },
  mounted() {
    this.updateFitSize();
    window.addEventListener("resize", this.onChartWindowResize, false);
    document.addEventListener("fullscreenchange", this.onChartFullscreenChange);
    document.addEventListener("webkitfullscreenchange", this.onChartFullscreenChange);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onChartWindowResize, false);
    document.removeEventListener("fullscreenchange", this.onChartFullscreenChange);
    document.removeEventListener("webkitfullscreenchange", this.onChartFullscreenChange);
  },
  methods: {
    formatDateTime(datetime) {
      let date = formatNumber(datetime.getFullYear(), 2, "0") + "-" + formatNumber(datetime.getMonth() + 1, 2, "0") + "-" + formatNumber(datetime.getDate(), 2, "0");
      let time = formatNumber(datetime.getHours(), 2, "0") + ":" + formatNumber(datetime.getMinutes(), 2, "0");
      return [date, time];
    },
    getLocalIsoTime() {
      var tzoffset = (new Date()).getTimezoneOffset() * 60000;
      return (new Date(Date.now() - tzoffset)).toISOString().slice(0, -1).replace(/[\:\.]/gi, "-");
    },
    getExactDateTimeString() {
      let datetime = this.formatDateTime(new Date());
      return `${datetime[0]} ${datetime[1]}`;
    },
    async runTasksWithConcurrency(taskFactories, concurrency) {
      if (taskFactories.length === 0) return;
      var limit = concurrency != null ? concurrency : 6;
      var index = 0;
      var workerCount = Math.min(limit, taskFactories.length);
      var workers = [];
      for (var w = 0; w < workerCount; w++) {
        workers.push((async function() {
          while (index < taskFactories.length) {
            var currentIndex = index++;
            await taskFactories[currentIndex]();
          }
        })());
      }
      await Promise.all(workers);
    },
    clearLoadedTrendData() {
      this.dataCollection = { datasets: [] };
      this.customSeriesCache = [];
      this.consumptionTrendsCache = [];
    },
    buildCustomSeriesList() {
      var list = this.selectedTrends.filter(function(x) { return x && x.variable; });
      list.sort(this.trendsSortComparer);
      return list;
    },
    getAllTrendsForExport() {
      var list = this.selectedTrends.filter(function(x) { return x && x.code != null; });
      list.sort(this.trendsSortComparer);
      return list;
    },
    onListedTrendsFiltered(filteredItems) {
      this.listedTrendsTotalRows = filteredItems.length;
      this.listedTrendsCurrentPage = 1;
    },
    showPresetGraphLimitError(activeCount) {
      this.presetGraphError = this.$i18n.t("project.graphViewer.presetGraphTooMany", {
        max: this.maxPresetGraphTrends,
        count: activeCount
      });
      this.showPresetGraphTooManyModal = true;
    },
    onDrawGraphClick() {
      this.presetGraphError = null;
      this.showPresetGraphTooManyModal = false;
      if (this.graphMode === "custom") {
        this.drawCustomGraph(true);
        return;
      }
      if (this.graphMode === "day" || this.graphMode === "three") {
        this.loadPresetTrendsToGraph();
      }
    },
    async drawCustomGraph(setupData) {
      if (this.presetGraphLoading || this.presetLoading || this.exportInProgress) return;
      if (this.selectedTrends.length > this.maxPresetGraphTrends) {
        this.showPresetGraphLimitError(this.selectedTrends.length);
        return;
      }
      await this.drawCustomData(setupData);
    },
    async loadPresetTrendsToGraph() {
      if (this.presetGraphLoading || this.presetLoading || this.exportInProgress) return;
      if (this.graphMode !== "day" && this.graphMode !== "three") return;

      var graphTrends = this.selectedTrends.filter(function(item) { return item.variable; });
      if (graphTrends.length === 0) return;

      if (graphTrends.length > this.maxPresetGraphTrends) {
        this.showPresetGraphLimitError(graphTrends.length);
        return;
      }

      this.informed = null;
      this.errored = null;
      this.presetGraphError = null;
      this.showPresetGraphTooManyModal = false;
      this.presetGraphLoading = true;
      var self = this;

      try {
        var taskFactories = graphTrends.map(function(item) {
          return function() {
            return exportService
              .getDefaultTrends(self.projectId, item.variable)
              .then(function(response) {
                item.code = response.data.code;
                item.shortUserDesc = response.data.shortUserDesc;
                item.longUserDesc = response.data.longUserDesc;
                item.phUnit = response.data.phUnit;
                item.data = response.data.data;
                item.index = response.data.index;
              })
              .catch(function(error) {
                self.errored = errorHandler(error);
              });
          };
        });
        await this.runTasksWithConcurrency(taskFactories, 6);
        this.changeCollection();
      } finally {
        this.presetGraphLoading = false;
      }
    },
    getConsumptionPointValue(point) {
      if (point == null) return null;
      if (point.y != null) return point.y;
      if (point.Y != null) return point.Y;
      return null;
    },
    applyConsumptionDataToTrends(allTrends, items) {
      var byCode = new Map();
      for (var i = 0; i < items.length; i++) {
        var item = items[i];
        var code = item.code != null ? item.code : item.Code;
        var data = item.data != null ? item.data : item.Data;
        byCode.set(code, data != null ? data : []);
      }
      for (var j = 0; j < allTrends.length; j++) {
        var trendData = byCode.get(allTrends[j].code);
        allTrends[j].custom = trendData != null ? trendData : [];
      }
    },
    async downloadConsumptionData() {
      this.errored = null;
      if (!this.dateFrom || !this.timeFrom || !this.dateTo || !this.timeTo)
        return false;
      if (
        this.dateFrom > this.dateTo ||
        (this.dateFrom == this.dateTo && this.timeFrom >= this.timeTo)
      )
        return false;

      var allTrends = this.getAllTrendsForExport();
      var codes = allTrends.map(function(x) { return x.code; });
      var from = this.dateFrom + "T" + this.timeFrom;
      var to = this.dateTo + "T" + this.timeTo;
      var jsonData = JSON.stringify({ codes: codes, from: from, to: to });

      try {
        var response = await exportService.getTrendsConsumption(this.projectId, jsonData);
        this.applyConsumptionDataToTrends(allTrends, response.data);
        this.consumptionTrendsCache = allTrends;
        return true;
      } catch (error) {
        this.errored = errorHandler(error);
        this.consumptionTrendsCache = [];
        return false;
      }
    },
    validateCustomDateRange() {
      if (!this.dateFrom || !this.timeFrom || !this.dateTo || !this.timeTo) return false;
      if (
        this.dateFrom > this.dateTo ||
        (this.dateFrom == this.dateTo && this.timeFrom >= this.timeTo)
      ) {
        return false;
      }
      return true;
    },
    async downloadCustomData(setupData) {
      return this.drawCustomData(setupData);
    },
    async drawCustomData(setupData) {
      this.errored = null;
      if (!this.validateCustomDateRange()) return false;

      var seriesList = this.buildCustomSeriesList();
      if (seriesList.length === 0) return false;

      this.customSeriesCache = seriesList;
      this.presetGraphLoading = true;
      var self = this;

      try {
        var taskFactories = seriesList.map(function(entry) {
          return function() {
            return exportService
              .getCustomTrends(
                self.projectId,
                entry.variable,
                self.dateFrom,
                self.timeFrom,
                self.dateTo,
                self.timeTo
              )
              .then(function(response) {
                entry.code = response.data.code;
                entry.shortUserDesc = response.data.shortUserDesc;
                entry.longUserDesc = response.data.longUserDesc;
                entry.phUnit = response.data.phUnit;
                entry.sortOrder = entry.sortOrder != null ? entry.sortOrder : 0;
                entry.custom = response.data.data;
              })
              .catch(function(error) {
                self.errored = errorHandler(error);
              });
          };
        });

        await this.runTasksWithConcurrency(taskFactories, 6);

        if (setupData) {
          self.selectedTrends = seriesList;
          self.setupData();
        }
        return true;
      } finally {
        this.presetGraphLoading = false;
      }
    },
    buildCustomExportCsv(allTrends) {
      var delimeter = this.exportDelimeter;
      var csvContent = this.$i18n.t("project.graphViewer.exportFileHeader") + " " + this.dateFrom + " " + this.timeFrom + " - " + this.dateTo + " " + this.timeTo + "\n";
      csvContent += this.$i18n.t("project.graphViewer.exportHeaderDate");

      var map = new Map();
      for (var i = 0; i < allTrends.length; i++) {
        csvContent += delimeter + allTrends[i].code + "-" + this.getDescription(allTrends[i]) + "[" + allTrends[i].phUnit + "]";
        if (allTrends[i].custom === null || allTrends[i].custom === undefined) continue;
        for (var j = 0; j < allTrends[i].custom.length; j++) {
          var date = (new Date(allTrends[i].custom[j].x + "Z").toUTCString()).replace(/[\,\;]/gi, "");
          if (!map.has(date)) map.set(date, []);
          map.get(date).push({ trendIndex: i, value: allTrends[i].custom[j].y });
        }
      }
      csvContent += "\n";

      var sortedEntries = Array.from(map.entries()).sort(function(a, b) {
        return new Date(a[0]) - new Date(b[0]);
      });

      for (var r = 0; r < sortedEntries.length; r++) {
        var key = sortedEntries[r][0];
        var value = sortedEntries[r][1];
        csvContent += key + delimeter;
        var it = 0;
        if (value === null || value === undefined) continue;
        for (var v = 0; v < value.length; v++) {
          var obj = value[v];
          if (it != obj.trendIndex) {
            var gap = obj.trendIndex - it;
            for (var g = 0; g < gap; g++) {
              csvContent += delimeter;
            }
          }
          csvContent += obj.value.toFixed(3) + delimeter;
          it = obj.trendIndex + 1;
        }
        csvContent += "\n";
      }

      return csvContent;
    },
    async exportCustomData() {
      if (this.exportInProgress || this.presetLoading) return;
      this.exportInProgress = true;
      this.presetGraphError = null;
      this.showPresetGraphTooManyModal = false;
      try {
        var loaded = await this.drawCustomData(false);
        if (!loaded) return;

        var allTrends = this.customSeriesCache.length > 0
          ? this.customSeriesCache
          : this.buildCustomSeriesList();
        var csvContent = this.buildCustomExportCsv(allTrends);
        var tzoffset = (new Date()).getTimezoneOffset() * 60000;
        var localISOTime = (new Date(Date.now() - tzoffset)).toISOString().slice(0, -1).replace(/[\:\.]/gi, "-");
        var fileName = "tronic_viewer_export_" + localISOTime + ".csv";
        csvExport(csvContent, fileName);
      } finally {
        this.exportInProgress = false;
      }
    },
    async exportPdfData(isCsv = false) {
      if (this.exportInProgress || this.presetLoading) return;
      this.exportInProgress = true;
      try {
        const loaded = await this.downloadConsumptionData();
        if (!loaded) return;

        var localISOTime = this.getLocalIsoTime();
        const fileName = `tronic_viewer_consumption_report_${localISOTime}`;
        var projectName = this.projectName != null ? this.projectName : this.projectId.toString();
        var presetName = this.presetName;
        var currentUser = (JSON.parse(localStorage.getItem("viewer-user"))).username;
        var dateFrom = `${this.dateFrom} ${this.timeFrom}`;
        var dateTo = `${this.dateTo} ${this.timeTo}`;
        var table = [];
        var total = 0;

        var allTrends = this.consumptionTrendsCache.length > 0
          ? this.consumptionTrendsCache
          : this.getAllTrendsForExport();
        allTrends.sort(this.trendsSortComparer);

        for (var i = 0; i < allTrends.length; i++) {
          var desc = this.getDescription(allTrends[i]);
          var before = 0;
          var after = 0;
          if (allTrends[i].custom != null && allTrends[i].custom.length > 0) {
            var firstVal = this.getConsumptionPointValue(allTrends[i].custom[0]);
            var lastVal = this.getConsumptionPointValue(allTrends[i].custom[allTrends[i].custom.length - 1]);
            before = firstVal != null ? firstVal : 0;
            after = lastVal != null ? lastVal : 0;
          }
          let sum = after - before;
          sum = Math.round((sum + Number.EPSILON) * 1000) / 1000;
          total += sum;
          table.push([allTrends[i].code, desc, before.toFixed(3), after.toFixed(3), sum.toFixed(3), allTrends[i].phUnit]);
        }

        total = Math.round((total + Number.EPSILON) * 1000) / 1000;
        const totalUnit = allTrends.length > 0 ? allTrends[0].phUnit : "";
        const totalLabel = `${total.toFixed(3)} ${totalUnit}`;
        const generatedAt = this.getExactDateTimeString();
        if (isCsv) {
          createCsvInvoice(false, fileName, projectName, presetName, currentUser, dateFrom, dateTo, table, totalLabel, generatedAt, this.exportDelimeter);
        } else {
          await createInvoice(false, fileName, projectName, presetName, currentUser, dateFrom, dateTo, table, totalLabel, generatedAt);
        }
      } finally {
        this.exportInProgress = false;
      }
    },
    async exportPdfDataReading(isCsv = false) {
      if (this.exportInProgress || this.presetLoading) return;
      this.exportInProgress = true;
      try {
        this.errored = null;
        if (!this.dateFrom || !this.timeFrom) return;

        let allTrends = this.getAllTrendsForExport();
        allTrends.sort(this.trendsSortComparer);
        let trendCodes = allTrends.map((x) => x.code);

        let date = this.dateFrom + "T" + this.timeFrom;
        let jsonData = JSON.stringify({ trends: trendCodes, date: date, threshold: this.readingThreshold });
        let results = null;

        try {
          const response = await exportService.getTrendsReading(this.projectId, jsonData);
          results = response.data;
        } catch (error) {
          this.errored = errorHandler(error);
          return;
        }

        if (results === null) return;
        let table = [];
        for (let i = 0; i < results.length; i++) {
          let desc = this.getDescription(results[i]);
          let sumString = results[i].trend != null ? (Math.round((results[i].trend.value + Number.EPSILON) * 1000) / 1000).toFixed(3) : "-";
          table.push([results[i].code, desc, "", "", sumString, results[i].phUnit]);
        }

        var localISOTime = this.getLocalIsoTime();
        const fileName = `tronic_viewer_reading_report_${localISOTime}`;
        var projectName = this.projectName != null ? this.projectName : this.projectId.toString();
        var presetName = this.presetName;
        var currentUser = (JSON.parse(localStorage.getItem("viewer-user"))).username;
        const readingDate = `${this.dateFrom} ${this.timeFrom}`;
        const generatedAt = this.getExactDateTimeString();

        if (isCsv) {
          createCsvInvoice(true, fileName, projectName, presetName, currentUser, readingDate, "", table, "", generatedAt, this.exportDelimeter);
        } else {
          await createInvoice(true, fileName, projectName, presetName, currentUser, readingDate, "", table, "", generatedAt);
        }
      } finally {
        this.exportInProgress = false;
      }
    },
    trendsSortComparer(a, b) {
      if (a === undefined && b === undefined) return 0;
      if (a === undefined) return 1;
      if (b === undefined) return -1;
      if (a.sortOrder != b.sortOrder) return a.sortOrder - b.sortOrder;
      if (a.code != b.code) return a.code - b.code;
      var descA = this.getDescription(a);
      var descB = this.getDescription(b);
      if (descA < descB) return -1;
      if (descA > descB) return 1;
      return 0;
    },
    getDescription(trend) {
      if (trend.shortUserDesc) return trend.shortUserDesc;
      return trend.longUserDesc;
    },
    getColor(trendCode) {
      let color = this.trendColors.get(trendCode);
      if (color != undefined) return color;
      let unused = colors.filter((x) => !this.usedColors.includes(x));
      if (unused.length == 0) return "gray";
      let newColor = unused[getRandomInt(unused.length)];
      this.usedColors.push(newColor);
      this.trendColors.set(trendCode, newColor);
      return newColor;
    },
    createChartObject(newData, trend) {
      let pickedColor = this.getColor(trend.code);
      return {
        label: this.getDescription(trend),
        data: newData,
        borderColor: pickedColor,
        backgroundColor: "transparent",
        pointBackgroundColor: pickedColor,
        borderWidth: 2,
        lineTension: 0.1,
        showLine: true,
        spanGaps: false,
        pointRadius: 5,
        pointHoverRadius: 6,
        fill: false
      };
    },
    createDataObjectAndPush(newData, trend) {
      let newDataObject = this.createChartObject(newData, trend);
      this.dataCollection.datasets.push(newDataObject);
    },
    setupData() {
      this.informed = null;
      if (this.graphMode === "custom") {
        this.dataCollection.datasets = [];
        let maxCount = 11;
        let counter = 0;
        this.selectedTrends.forEach((trend) => {
          if (!trend.custom) return;
          if (counter >= maxCount - 1) {
            this.informed = `${this.$i18n.t("project.graphViewer.maxGraphDraw")}: ${maxCount}`;
            return;
          }
          this.createDataObjectAndPush(trend.custom, trend);
          counter++;
        });
        this.updateFitSize();
        return;
      }
      this.changeCollection();
    },
    changeCollection() {
      this.dataCollection.datasets = [];
      if (this.graphMode === "day") {
        this.selectedTrends.forEach((trend) => {
          if (!trend.data) return;
          let newData = trend.data.slice(
            trend.index,
            trend.data.length
          );
          this.createDataObjectAndPush(newData, trend);
        });
      } else {
        this.selectedTrends.forEach((trend) => {
          if (!trend.data) return;
          this.createDataObjectAndPush(trend.data, trend);
        });
      }
      this.updateFitSize();
    },
    removeFromExtraTrends(item) {
      if (!item || !item.variable) return;
      this.selectedTrends = this.selectedTrends.filter(function(t) { return t.variable !== item.variable; });
      this.$emit("remove-selected-trend", item.variable);
      this.clearLoadedTrendData();
    },
    clearTrendSelection() {
      this.selectedTrends = [];
      this.$emit("clear-selected-trends");
      this.clearLoadedTrendData();
    },
    setChartPreconfiguration(config) {
      if (config.width != undefined) this.graphCustomWidth = config.width;
      if (config.height != undefined) this.graphCustomHeight = config.height;
      this.setSize(false);

      if (config.thresholds[0] != undefined) this.yThresholdMin1 = config.thresholds[0];
      if (config.thresholds[1] != undefined) this.yThresholdMin2 = config.thresholds[1];
      if (config.thresholds[2] != undefined) this.yThresholdMax1 = config.thresholds[2];
      if (config.thresholds[3] != undefined) this.yThresholdMax2 = config.thresholds[3];
      this.setThresholds();

      if (config.ymin != undefined) this.yGraphMinimum = config.ymin;
      if (config.ymax != undefined) this.yGraphMaximum = config.ymax;
      this.setRange(config.ymin, config.ymax);
    },
    setRange(yMin, yMax) {
      this.$refs.lineChartRef.setYRange(yMin, yMax);
    },
    clearRange() {
      this.$refs.lineChartRef.clearYRange();
    },
    toggleZoom() {
      this.toggleZoomFlag = !this.toggleZoomFlag;
      this.$refs.lineChartRef.toggleZoom(this.toggleZoomFlag);
    },
    resetZoom() {
      this.$refs.lineChartRef.resetZoom();
    },
    toggleFullScreen() {
      const wrapper = this.$refs.graphDiv;

      if (!document.fullscreenElement) {
        if (wrapper.requestFullscreen) wrapper.requestFullscreen();
        else if (wrapper.webkitRequestFullscreen) wrapper.webkitRequestFullscreen();
      } else {
        if (document.exitFullscreen) document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      }

      setTimeout(() => this.updateFitSize(), 300);
    },
    resizeChartBox() {
      const chart = this.$refs.lineChartRef && this.$refs.lineChartRef.$data._chart;
      if (chart) chart.resize();
    },
    isSetPixel(val) {
      return val != null && val !== "" && !isNaN(Number(val)) && Number(val) > 0;
    },
    onChartWindowResize() {
      this.updateFitSize();
    },
    onChartFullscreenChange() {
      this.updateFitSize();
    },
    getFitChartHeight() {
      const chartEl = this.$refs.lineChartRef && this.$refs.lineChartRef.$el;
      const el = chartEl || this.$refs.graphDiv;
      if (!el) return this.fitChartHeight || 320;

      const wrapper = this.$refs.graphDiv;
      const isFs = wrapper && (document.fullscreenElement === wrapper || document.webkitFullscreenElement === wrapper);
      const viewBottom = isFs ? wrapper.getBoundingClientRect().bottom : window.innerHeight;
      const viewHeight = isFs ? wrapper.clientHeight : window.innerHeight;
      const top = el.getBoundingClientRect().top;
      const available = Math.floor(viewBottom - top - 12);
      const minH = Math.max(280, Math.round(viewHeight * 0.42));
      return Math.max(minH, available);
    },
    updateFitSize() {
      this.$nextTick(() => {
        if (!this.$refs.graphDiv) return;
        if (!this.lockCustomHeight) {
          this.fitChartHeight = this.getFitChartHeight();
        }
        this.$nextTick(() => {
          const chart = this.$refs.lineChartRef && this.$refs.lineChartRef.$data._chart;
          if (chart) chart.resize();
        });
      });
    },
    setSize(toDefault) {
      if (toDefault) {
        this.lockCustomWidth = false;
        this.lockCustomHeight = false;
        this.graphCustomWidthConst = null;
        this.graphCustomHeightConst = null;
      } else {
        const hasW = this.isSetPixel(this.graphCustomWidth);
        const hasH = this.isSetPixel(this.graphCustomHeight);
        this.lockCustomWidth = hasW;
        this.lockCustomHeight = hasH;
        this.graphCustomWidthConst = hasW ? Number(this.graphCustomWidth) : null;
        this.graphCustomHeightConst = hasH ? Number(this.graphCustomHeight) : null;
      }
      this.updateFitSize();
    },
    printGraph() {
      const chart = this.$refs.lineChartRef.$data._chart;
      if (!chart) return;

      let projectName = this.projectName != null ? this.projectName : this.projectId.toString();
      const imageUrl = chart.toBase64Image();

      const win = window.open('', '_blank');
      win.document.write(`
      <html>
        <head>
          <title>Tronic Control Vizleda Viewer - ${projectName}</title>
          <style>
            body { margin: 0; text-align: center; background: #fff; }
            img { max-width: 100%; height: auto; }
          </style>
        </head>
        <body>
          <img src="${imageUrl}" onload="window.print();window.close()" />
        </body>
      </html>
    `);
      win.document.close();
    },
    downloadGraph() {
      const chart = this.$refs.lineChartRef.$data._chart;
      if (!chart) return;

      const canvas = chart.canvas;
      const mime = 'image/png';
      const url = canvas.toDataURL(mime);

      const a = document.createElement('a');
      a.href = url;
      a.download = `graph_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.png`;
      a.click();
    },
    setThresholds() {
      const chart = this.$refs.lineChartRef.$data._chart;
      if (!chart) return;

      chart.config.options.customThresholds = [
        this.yThresholdMax1,
        this.yThresholdMax2,
        this.yThresholdMin1,
        this.yThresholdMin2
      ];
      chart.update({ duration: 0 });
    }
  },
};
</script>

<style scoped>
.chart-panel-toggle {
  font-size: 0.8rem;
  line-height: 1.2;
}
.chart-fit {
  display: block;
  box-sizing: border-box;
  overflow: hidden;
  position: relative;
}
</style>
